<template>
  <div v-if="data" class="flex flex-col sm:flex-row gap-6">
    <div class="flex flex-col gap-6 flex-1 min-w-0">
      <button
        ref="effRef"
        type="button"
        class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg text-left cursor-help w-full"
        aria-describedby="hitgroup-eff-tip"
        @mouseenter="effOpen = true"
        @mouseleave="effOpen = false"
        @focus="effOpen = true"
        @blur="effOpen = false"
      >
        <div class="px-5 py-4 lg:px-4 lg:py-3 text-text-primary w-full">{{ $t('coach.hitgroup.efficiency_title') }}</div>
        <div class="p-4 w-full">
          <div class="flex items-baseline gap-2.5 flex-wrap">
            <span class="text-primary-300 text-3xl font-bold tabular-nums">{{ data.efficiency == null ? '—' : `${Math.round(data.efficiency)}%` }}</span>
            <span v-if="effDelta" class="text-xs tabular-nums rounded px-1 py-0.5" :class="effDelta.class">{{ effDelta.text }}</span>
          </div>
          <p class="text-xs text-gray-600 mt-2 leading-relaxed">
            {{ $t('coach.hitgroup.efficiency_description') }}
          </p>
        </div>
      </button>

      <div v-if="breakdownRows.length" class="flex flex-col divide-y divide-gray-950 bg-gray-900 rounded-lg flex-1">
        <div class="px-5 py-4 lg:px-4 lg:py-3 text-text-primary">{{ $t('coach.hitgroup.breakdown_title') }}</div>
        <div class="p-4 flex-1">
          <div
            v-for="gap in breakdownRows"
            :key="gap.region.key"
            class="flex items-center gap-2 py-2 first:pt-0 border-b border-gray-950 last:border-b-0 text-sm"
          >
            <span class="text-gray-300 flex-1 truncate">{{ gap.region.label }}</span>
            <span class="text-[11px] text-gray-600 tabular-nums whitespace-nowrap">{{ gap.region.hitShare.toFixed(1) }}% vs {{ gap.region.peerShare.toFixed(1) }}%</span>
            <span
              class="text-xs tabular-nums rounded px-1 py-0.5"
              :class="gap.tone === 'good' ? 'text-success-400 bg-success-400/10' : gap.tone === 'bad' ? 'text-danger-400 bg-danger-400/10' : 'text-gray-500'"
            >{{ gap.diff >= 0 ? '+' : '−' }}{{ Math.abs(gap.diff).toFixed(1) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-gray-900 rounded-lg p-4 w-full sm:max-w-[260px] shrink-0 flex flex-col gap-3">
      <div class="flex items-center justify-center">
        <div class="flex bg-gray-950 border border-gray-800 rounded-md p-0.5" role="group" :aria-label="$t('coach.hitgroup.measure')">
          <button
            v-for="option in MEASURES"
            :key="option.key"
            type="button"
            class="text-xs font-semibold px-3 py-1 rounded"
            :class="measure === option.key ? 'bg-gray-800 text-text-primary' : 'text-text-secondary'"
            :aria-pressed="measure === option.key"
            @click="measure = option.key"
          >{{ $t(option.label) }}</button>
        </div>
      </div>
      <HitgroupFigure :regions="data.regions" :measure="measure" />
      <p class="text-xs text-gray-600 text-center">
        {{ $t('coach.hitgroup.figure_caption', { n: data.totalHits }) }}
      </p>
    </div>

    <Teleport to="body">
      <div v-if="effOpen" id="hitgroup-eff-tip" class="hg-tip" :style="effTipStyle" role="tooltip">
        <div class="flex items-baseline justify-between font-semibold mb-1.5">
          <span>{{ $t('coach.hitgroup.tip_title') }}</span>
          <span class="tabular-nums">{{ Math.round(data.efficiency) }}%</span>
        </div>
        <table class="w-full text-xs">
          <tbody>
            <tr v-for="region in scoredRegions" :key="region.key">
              <td class="text-gray-400 py-0.5">{{ region.label }} {{ region.hitCount }} × {{ data.multipliers[region.key].toFixed(2) }}</td>
              <td class="hg-num">{{ (region.hitCount * data.multipliers[region.key]).toFixed(1) }}</td>
            </tr>
            <tr><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.per_hits', { n: data.totalHits }) }}</td><td class="hg-num">{{ ((data.efficiency / 100) * data.divisor).toFixed(2) }}×</td></tr>
            <tr><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.per_maximum', { n: data.divisor }) }}</td><td class="hg-num">{{ Math.round(data.efficiency) }}%</td></tr>
            <tr v-if="data.peerEfficiency != null"><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.bracket_average') }}</td><td class="hg-num">{{ Math.round(data.peerEfficiency) }}%</td></tr>
          </tbody>
        </table>
        <p class="mt-2 text-[11px] leading-snug text-gray-500">
          {{ $t('coach.hitgroup.excluded_note') }}
        </p>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import HitgroupFigure from './HitgroupFigure.vue';
import useHitgroups from '../../composables/useHitgroups.js';

const props = defineProps({
  // The selected player's stat map from the match payload.
  values: { type: Object, default: null },
});

const MEASURES = [
  { key: 'hits', label: 'coach.hitgroup.measure_hits' },
  { key: 'damage', label: 'coach.hitgroup.measure_damage' },
];
const DELTA_CLASSES = {
  flat: 'text-gray-500',
  good: 'text-success-400 bg-success-400/10',
  bad: 'text-danger-400 bg-danger-400/10',
};

const measure = ref('hits');
const effOpen = ref(false);
const effRef = ref(null);

const {
  data, scoredRegions, effDelta: effGap, breakdownRows,
} = useHitgroups(() => props.values);

const effDelta = computed(() => effGap.value && {
  text: `${effGap.value.diff >= 0 ? '+' : '−'}${Math.abs(effGap.value.diff).toFixed(0)}%`,
  class: DELTA_CLASSES[effGap.value.tone],
});

const effTipStyle = ref({});
watchEffect(() => {
  if (!effOpen.value || !effRef.value) return;
  const rect = effRef.value.getBoundingClientRect();
  const width = 240;
  effTipStyle.value = {
    left: `${Math.max(10, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 10))}px`,
    top: `${rect.bottom + 10}px`,
    transform: 'none',
  };
});
</script>
